/**
 * Documents API
 *
 * Wraps document upload (`/api/v1/documents/upload`) and the analysis
 * kickoff endpoint (`/{fileId}/start-analysis-async`). The shared axios
 * client has baseURL `/api/v1`; the start-analysis URL is OUTSIDE that
 * prefix, so it overrides baseURL to `''` per call.
 */

import client from './client';
import { config } from '../config/env';

const NO_API_V1 = { baseURL: '' };

/**
 * Build the URL that serves the ORIGINAL uploaded file (not the converted
 * markdown) for a given file_id. Same-origin and cookie-authenticated, so it
 * can be opened directly in a new tab / iframe for inline viewing.
 * Pass `download` to force a save instead of inline viewing.
 *
 * @param {string} fileId
 * @param {{ download?: boolean }} [opts]
 * @returns {string}
 */
export function getOriginalDocumentUrl(fileId, { download = false } = {}) {
  const base = `${config.API_BASE_URL}/documents/${encodeURIComponent(fileId)}/original`;
  return download ? `${base}?download=1` : base;
}

/**
 * Upload a document file to the Athena backend.
 * Mirrors AthenaBackendService.uploadDocument(file, sessionId, tenantId).
 *
 * Builds a multipart/form-data body. The shared axios client has an
 * instance-level `Content-Type: application/json` default — for FormData
 * we must override per-request so axios sets `multipart/form-data` with
 * the correct boundary. Without the override, axios JSON-stringifies the
 * FormData (yielding `{file: {}, session_id, tenant_id}`) and the backend
 * rejects with 422 ("Field required" for session_id + file).
 */
export async function uploadDocument(file, sessionId, tenantId) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('session_id', sessionId);
  formData.append('tenant_id', tenantId);
  return client.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/**
 * Start the async TARA analysis workflow for an uploaded document.
 * Mirrors AthenaBackendService.startAnalysis(sessionId, tenantId, fileIds).
 *
 * The URL `/{fileId}/start-analysis-async` is exposed at the FastAPI root
 * (no `/api/v1` prefix), so the call overrides baseURL to `''`.
 *
 * @param {string} fileId         - The file_id returned from uploadDocument.
 * @param {string} sessionId
 * @param {string} tenantId
 * @param {string[]} [fileIds]    - Optional list for multi-document analysis.
 */
export async function startAnalysis(fileId, sessionId, tenantId, fileIds = null) {
  const body = {
    session_id: sessionId,
    tenant_id: tenantId,
  };
  if (fileIds && fileIds.length > 0) {
    body.file_ids = fileIds;
  }
  return client.post(`/${fileId}/start-analysis-async`, body, NO_API_V1);
}
